import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldAlert, FileText, Sparkles, Globe } from 'lucide-react';
import { AtomicStep, AuditInstance, SEOModule, EvidenceItem } from '../../types';
import { EngineService, ValidationError } from '../../services/engine';
import { ScoringEngine } from '../../services/scoringEngine';
import { ExplanationCard } from './ExplanationCard';
import { DataForm } from './DataForm';
import { CalculationBadge } from './CalculationBadge';
import { EvidenceUploader } from './EvidenceUploader';
import { Button } from '../UI/Button';

interface StepWorkspaceProps {
  step: AtomicStep;
  audit: AuditInstance;
  module: SEOModule;
  onSaveAnswer: (stepId: string, fieldValues: Record<string, any>, calculatedValues: Record<string, any>, evidence?: EvidenceItem, isComplete?: boolean) => void;
  onNavigateStep: (stepId: string) => void;
  onNavigateReport?: () => void;
}

export const StepWorkspace: React.FC<StepWorkspaceProps> = ({
  step,
  audit,
  module,
  onSaveAnswer,
  onNavigateStep,
  onNavigateReport,
}) => {
  const existingAnswer = audit.answers[step.id];

  const [fieldValues, setFieldValues] = useState<Record<string, any>>(
    existingAnswer?.fieldValues || {}
  );
  const [evidence, setEvidence] = useState<EvidenceItem | undefined>(
    existingAnswer?.evidence
  );
  const [, setErrors] = useState<ValidationError[]>([]);

  // Update local state when step changes
  useEffect(() => {
    const ans = audit.answers[step.id];
    setFieldValues(ans?.fieldValues || {});
    setEvidence(ans?.evidence);
    setErrors([]);
  }, [step.id, audit]);

  const isSitelinerBypassed =
    step.id === 'step-siteliner' &&
    (fieldValues.sitelinerAccessStatus === 'blocked_bypass' ||
      fieldValues.sitelinerAccessStatus === 'unreachable_bypass');

  // Real-time validation & calculation
  const calculatedValues = EngineService.runCalculations(step, fieldValues, audit);
  const currentErrors = EngineService.validateStepFields(step, fieldValues, audit);
  const isComplete = EngineService.isStepComplete(
    step,
    fieldValues,
    currentErrors,
    { stepId: step.id, fieldValues, calculatedValues, evidence, completed: false }
  );

  // Auto-save & trigger update on change
  const handleFieldChange = (fieldId: string, value: any) => {
    const newFieldValues = { ...fieldValues, [fieldId]: value };
    setFieldValues(newFieldValues);

    const newErrors = EngineService.validateStepFields(step, newFieldValues, audit);
    setErrors(newErrors);

    const newCalculated = EngineService.runCalculations(step, newFieldValues, audit);
    const newIsComplete = EngineService.isStepComplete(
      step,
      newFieldValues,
      newErrors,
      { stepId: step.id, fieldValues: newFieldValues, calculatedValues: newCalculated, evidence, completed: false }
    );

    onSaveAnswer(step.id, newFieldValues, newCalculated, evidence, newIsComplete);
  };

  const handleEvidenceChange = (newEvidence?: EvidenceItem) => {
    setEvidence(newEvidence);
    const newIsComplete = EngineService.isStepComplete(
      step,
      fieldValues,
      currentErrors,
      { stepId: step.id, fieldValues, calculatedValues, evidence: newEvidence, completed: false }
    );
    onSaveAnswer(step.id, fieldValues, calculatedValues, newEvidence, newIsComplete);
  };

  // Find previous & next step IDs
  const allSteps: AtomicStep[] = [];
  module.sections.forEach(sec => sec.steps.forEach(st => allSteps.push(st)));
  const currentIndex = allSteps.findIndex(st => st.id === step.id);
  const prevStep = currentIndex > 0 ? allSteps[currentIndex - 1] : null;
  const nextStep = currentIndex < allSteps.length - 1 ? allSteps[currentIndex + 1] : null;
  const isFinalStep = currentIndex === allSteps.length - 1;

  // Calculate audit overall progress & health score
  let auditCompletedCount = 0;
  allSteps.forEach(st => {
    if (audit.answers[st.id]?.completed) auditCompletedCount++;
  });
  const auditProgressPct = allSteps.length > 0 ? Math.round((auditCompletedCount / allSteps.length) * 100) : 0;
  const auditScores = ScoringEngine.calculateScores(audit, module);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#38bdf8';
    if (score >= 40) return '#f59e0b';
    return '#f43f5e';
  };

  return (
    <div className="animate-fade-in" style={{ paddingBottom: '40px' }}>
      
      {/* STATIC AUDIT PROJECT HEADER */}
      <div
        style={{
          background: '#090e1a',
          border: '1px solid #1e293b',
          borderRadius: '16px',
          padding: '16px 24px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '10px', fontWeight: 800, color: '#f43f5e', background: 'rgba(244, 63, 94, 0.12)', border: '1px solid rgba(244, 63, 94, 0.3)', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase' }}>
              SEO CLIENT AUDIT
            </span>
            <span style={{ fontSize: '10px', fontWeight: 800, color: auditProgressPct >= 100 ? '#10b981' : '#38bdf8', background: auditProgressPct >= 100 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(2, 132, 199, 0.15)', padding: '2px 6px', borderRadius: '4px' }}>
              ● {auditProgressPct >= 100 ? 'COMPLETED' : 'IN PROGRESS'}
            </span>
          </div>

          <h2 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 800, margin: '0 0 2px 0' }}>
            {audit.clientName}
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#38bdf8', fontFamily: 'var(--font-mono)' }}>
            <Globe size={13} />
            <span>{audit.websiteUrl}</span>
          </div>
        </div>

        {/* Health Score & Progress Badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
              AUDIT PROGRESS
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '90px', height: '6px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${auditProgressPct}%`, height: '100%', background: 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)' }} />
              </div>
              <span style={{ fontSize: '12px', fontWeight: 800, color: '#ffffff', fontFamily: 'var(--font-mono)' }}>
                {auditProgressPct}%
              </span>
            </div>
          </div>

          <div style={{ background: '#070a12', border: '1px solid #1e293b', borderRadius: '8px', padding: '6px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: '9px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
              ABILITY SCORE
            </div>
            <div style={{ fontSize: '18px', fontWeight: 900, color: getScoreColor(auditScores.overallScore), fontFamily: 'var(--font-mono)' }}>
              {auditScores.overallScore}%
            </div>
          </div>
        </div>
      </div>

      {/* Step Title Header */}
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>
            STEP {step.stepNumber} OF {allSteps.length} · {step.tool}
          </div>
          <h2 style={{ fontSize: '26px', color: '#ffffff', fontWeight: 800, margin: 0 }}>
            {step.title}
          </h2>
        </div>

        {isComplete ? (
          <span style={{ fontSize: '11px', fontWeight: 800, color: '#10b981', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '4px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircle2 size={13} /> ✓ STEP COMPLETED
          </span>
        ) : (
          <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '4px 10px', borderRadius: '6px' }}>
            IN PROGRESS
          </span>
        )}
      </div>

      {/* Pedagogical What/Why/How Card */}
      <ExplanationCard step={step} />

      {/* Field Inputs */}
      <DataForm
        step={step}
        fieldValues={fieldValues}
        errors={currentErrors}
        onChange={handleFieldChange}
      />

      {/* Automatic Mathematics Calculations */}
      {step.calculations && step.calculations.length > 0 && !isSitelinerBypassed && (
        <CalculationBadge
          calculations={step.calculations}
          calculatedValues={calculatedValues}
        />
      )}

      {/* Evidence Upload */}
      {step.requiresEvidence && !isSitelinerBypassed && (
        <EvidenceUploader
          promptText={step.evidencePrompt}
          evidence={evidence}
          toolName={step.tool}
          onChange={handleEvidenceChange}
        />
      )}

      {/* Final Step Audit Completed Banner */}
      {isFinalStep && isComplete && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)',
            border: '1px solid #10b981',
            borderRadius: '16px',
            padding: '24px',
            textAlign: 'center',
            marginBottom: '30px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', color: '#10b981', fontWeight: 800, fontSize: '20px', marginBottom: '8px' }}>
            <Sparkles size={24} />
            <span>🎉 SEO Audit Completed!</span>
          </div>
          <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '16px' }}>
            All {allSteps.length} atomic audit steps have been completed for <strong>{audit.clientName}</strong> ({audit.websiteUrl}). Click below to view your full executive report and calculated scorecard.
          </p>
          <Button
            variant="success"
            size="lg"
            icon={<FileText size={20} />}
            onClick={onNavigateReport}
          >
            Generate Final Report & Scorecard →
          </Button>
        </div>
      )}

      {/* Step Completion Status Alert */}
      {!isFinalStep && (
        <div
          style={{
            background: isComplete ? 'rgba(16, 185, 129, 0.12)' : 'rgba(30, 41, 59, 0.6)',
            border: `1px solid ${isComplete ? 'rgba(16, 185, 129, 0.3)' : '#1e293b'}`,
            borderRadius: '12px',
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {isComplete ? (
              <CheckCircle2 size={18} color="#10b981" />
            ) : (
              <ShieldAlert size={18} color="#f59e0b" />
            )}
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: isComplete ? '#10b981' : '#94a3b8' }}>
                {isComplete ? '✓ Step Requirement Met — Automatically Saved' : 'Step Requirement Pending'}
              </div>
              <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                {isComplete
                  ? 'All required inputs validated. The next step is unlocked.'
                  : 'Enter all required valid inputs above to automatically complete this step.'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Next Step Preview Container */}
      {nextStep && (
        <div
          style={{
            background: '#070a12',
            border: '1px solid #1e293b',
            borderRadius: '12px',
            padding: '14px 18px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: '10px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '2px' }}>
              UP NEXT
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>
              Step {nextStep.stepNumber} · {nextStep.title}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={!isComplete}
            onClick={() => onNavigateStep(nextStep.id)}
          >
            Preview Next →
          </Button>
        </div>
      )}

      {/* STATIC BOTTOM NAVIGATION BAR */}
      <div
        style={{
          background: '#090e1a',
          border: '1px solid #1e293b',
          borderRadius: '12px',
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {prevStep ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<ArrowLeft size={15} />}
            onClick={() => onNavigateStep(prevStep.id)}
          >
            ← Previous Step
          </Button>
        ) : (
          <div />
        )}

        <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', fontFamily: 'var(--font-mono)' }}>
          Step {step.stepNumber} of {allSteps.length}
        </div>

        {nextStep ? (
          <Button
            variant="primary"
            size="sm"
            disabled={!isComplete}
            onClick={() => onNavigateStep(nextStep.id)}
          >
            <span>Save & Continue</span>
            <ArrowRight size={15} style={{ marginLeft: '6px' }} />
          </Button>
        ) : (
          <Button
            variant="success"
            size="sm"
            disabled={!isComplete}
            icon={<FileText size={16} />}
            onClick={onNavigateReport}
          >
            <span>Generate Final Report & Scorecard</span>
            <ArrowRight size={15} style={{ marginLeft: '6px' }} />
          </Button>
        )}
      </div>
    </div>
  );
};
